import { LwFormMultiSelect } from 'redesign';
import { useFormContext } from 'react-hook-form';
import { Chip, chipClasses, createFilterOptions, styled } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkerEmploymentType } from '@types';
import { PreferredWorkerFormContext } from '../preferred-worker.types';
import { getPreferredWorkerEmploymentTypeLabel } from '../../helpers/get-preferred-worker-employment-type-label';
import { MIN_PREFERRED_WORKER_SEARCH_LENGTH } from '../../hooks/use-get-preferred-worker-options';

export type WorkerOption = {
  name: string;
  id: string;
  description?: string;
  employmentType?: WorkerEmploymentType;
  workerType?: 'PLATFORM' | 'PARTNER';
};

type Props = {
  workerOptions: WorkerOption[];
  index: number;
  disabled?: boolean;
  isLoading?: boolean;
  onSearchChange?: (search: string) => void;
};

export const PreferredWorkerMultiSelect = ({
  workerOptions,
  disabled,
  index,
  isLoading,
  onSearchChange,
}: Props) => {
  const { control } = useFormContext<PreferredWorkerFormContext>();
  const { t } = useTranslation('web-common');
  const [inputValue, setInputValue] = useState('');
  const showSearchHint = inputValue.trim().length < MIN_PREFERRED_WORKER_SEARCH_LENGTH;

  const filterOptions = useCallback(
    createFilterOptions({
      stringify: ({ name, description, employmentType }: WorkerOption) => {
        if (!employmentType) {
          return `${name} ${description ?? ''}`;
        }
        const { key, ns } = getPreferredWorkerEmploymentTypeLabel(employmentType);
        return `${name} ${description ?? ''} ${t(key, { ns })}`;
      },
    }),
    [t]
  );

  const filterOptionsWithSearchLimit = useCallback(
    (options: WorkerOption[], state: Parameters<typeof filterOptions>[1]) => {
      if (state.inputValue.trim().length < MIN_PREFERRED_WORKER_SEARCH_LENGTH) {
        return [];
      }
      return filterOptions(options, state);
    },
    [filterOptions]
  );

  return (
    <LwFormMultiSelect
      name={`visibilityRules.${index}.workerOptions`}
      control={control}
      disabled={disabled}
      label={t('work-form.preferred-worker.worker-select.label')}
      rules={{ required: t('work-form.preferred-worker.worker-select.error') }}
      options={workerOptions}
      getOptionKey={(option: WorkerOption) => option.id}
      getOptionLabel={(option: WorkerOption) => option.name}
      renderOption={(props, option) => {
        const employmentTypeLabel = option.employmentType
          ? getPreferredWorkerEmploymentTypeLabel(option.employmentType)
          : null;

        return (
          <Option {...props} key={option.id}>
            <Chip label={option.name} />
            {option.description ? <CompanyName>{option.description}</CompanyName> : null}
            {employmentTypeLabel ? (
              <EmploymentTypeBadge>
                {t(employmentTypeLabel.key, { ns: employmentTypeLabel.ns })}
              </EmploymentTypeBadge>
            ) : null}
          </Option>
        );
      }}
      filterOptions={filterOptionsWithSearchLimit}
      isLoading={isLoading}
      onInputChange={(_event, value) => {
        setInputValue(value);
        onSearchChange?.(value);
      }}
      noOptionsText={
        showSearchHint
          ? t('work-form.preferred-worker.search-hint.text', {
              minLength: MIN_PREFERRED_WORKER_SEARCH_LENGTH,
            })
          : t('work-form.preferred-worker.empty-options.text')
      }
    />
  );
};

const Option = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  [`& .${chipClasses.root}`]: {
    backgroundColor: theme.palette.lwPrimaryAccentLight[10],
    marginBottom: 0,
    marginRight: 0,
    borderRadius: theme.spacing(6),
  },
  [`& .${chipClasses.label}`]: {
    color: theme.palette.lwPrimaryAccentLight[100],
  },
}));

const CompanyName = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: theme.typography.pxToRem(16),
  color: theme.palette.lwBlack[100],
}));

const EmploymentTypeBadge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.lwBlack[6],
  color: theme.palette.lwBlack[60],
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 600,
  lineHeight: theme.typography.pxToRem(16),
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.spacing(1),
  whiteSpace: 'nowrap',
}));
